import { apiWithPrefix as api } from '../utils/api';

/**
 * Claims Service
 * Handles all claims-related API calls
 */
export class ClaimsService {
  /**
   * Get user's claims
   */
  static async getClaims(filters = {}) {
    try {
      const response = await api.get('/claims', { params: filters });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch claims');
    }
  }

  /**
   * Get claims by manager (for approval)
   */
  static async getClaimsByManager() {
    try {
      const response = await api.get('/claims-by-manager');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch claims for manager');
    }
  }

  /**
   * Get manager-approved claims (for approver history tab)
   */
  static async getApprovedClaimsByManager() {
    try {
      const response = await api.get('/claims-approved-by-manager');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch approved claims');
    }
  }

  /**
   * Create new claim
   */
  static async createClaim(claimData) {
    try {
      const response = await api.post('/claims', claimData);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create claim');
    }
  }

  /**
   * Create claim with file upload
   */
  static async createClaimWithFile(formData) {
    try {
      const response = await api.post('/claims', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to create claim with file');
    }
  }

  /**
   * Update claim status
   */
  static async updateClaimStatus(id, data) {
    try {
      const response = await api.put(`/claims/${id}`, data);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to update claim status');
    }
  }

  /**
   * Get claims awaiting finance payment (admin/HR)
   */
  static async getProcessingClaims() {
    try {
      const response = await api.get('/claims/processing');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch processing claims');
    }
  }

  /**
   * Get completed refunds (accountant)
   */
  static async getCompletedRefundClaims() {
    try {
      const response = await api.get('/claims/completed-refunds');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch completed refunds');
    }
  }

  /**
   * Mark claim refund as completed (accountant)
   */
  static async markClaimPaid(id, formData) {
    try {
      const response = await api.put(`/claims/${id}/mark-paid`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to mark claim as paid');
    }
  }

  static async getReceiptViewUrl(id, paymentReceipt = false) {
    try {
      const path = paymentReceipt ? `/claims/${id}/payment-receipt` : `/claims/${id}/receipt`;
      const response = await api.get(path);
      return response.data.url;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to open receipt');
    }
  }

  /**
   * Get claim by ID
   */
  static async getClaimById(id) {
    try {
      const response = await api.get(`/claims/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch claim');
    }
  }

  /**
   * Delete claim
   */
  static async deleteClaim(id) {
    try {
      const response = await api.delete(`/claims/${id}`);
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to delete claim');
    }
  }

  /**
   * Get claim statistics
   */
  static async getClaimStats(period = 'month') {
    try {
      const response = await api.get('/claims/stats', {
        params: { period }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch claim statistics');
    }
  }

  /**
   * Get claim categories
   */
  static async getClaimCategories() {
    try {
      const response = await api.get('/claims/categories');
      return response.data;
    } catch (error) {
      // Return default categories if API fails
      return [
        'Travel',
        'Meal',
        'Internet',
        'Medical',
        'Office Supplies',
        'Training',
        'Other'
      ];
    }
  }

  /**
   * Upload receipt file
   */
  static async uploadReceipt(file) {
    try {
      const formData = new FormData();
      formData.append('receipt', file);
      
      const response = await api.post('/claims/upload-receipt', formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to upload receipt');
    }
  }

  /**
   * Download receipt
   */
  static async downloadReceipt(claimId) {
    try {
      const response = await api.get(`/claims/${claimId}/receipt`, {
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to download receipt');
    }
  }

  /**
   * Export claims report
   */
  static async exportClaimsReport(startDate, endDate, format = 'excel') {
    try {
      const response = await api.get('/claims/export', {
        params: { startDate, endDate, format },
        responseType: 'blob'
      });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to export claims report');
    }
  }

  /**
   * Get pending claims count
   */
  static async getPendingClaimsCount() {
    try {
      const response = await api.get('/claims/pending-count');
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to fetch pending claims count');
    }
  }

  /**
   * Bulk approve claims
   */
  static async bulkApproveClaims(claimIds) {
    try {
      const response = await api.post('/claims/bulk-approve', { claimIds });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to bulk approve claims');
    }
  }

  /**
   * Bulk reject claims
   */
  static async bulkRejectClaims(claimIds, reason) {
    try {
      const response = await api.post('/claims/bulk-reject', { claimIds, reason });
      return response.data;
    } catch (error) {
      throw new Error(error.response?.data?.message || 'Failed to bulk reject claims');
    }
  }

  /**
   * Validate claim amount
   */
  static validateClaimAmount(amount, type) {
    const limits = {
      'Travel': 50000,
      'Meal': 5000,
      'Internet': 2000,
      'Medical': 25000,
      'Office Supplies': 10000,
      'Training': 100000,
      'Other': 10000
    };

    const limit = limits[type] || 10000;
    return amount <= limit;
  }

  /**
   * Format currency
   */
  static formatCurrency(amount) {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR'
    }).format(amount);
  }
}

export default ClaimsService;
